import { useMemo, useState } from 'react'
import { cn } from './cnNew'
import { Checkbox, Input } from './controlsNew'
import { Button } from './buttonNew'
import { Popover } from './popoverNew'
import { ChevronDownIcon, SearchIcon } from './iconsNew'

/**
 * Selección múltiple con búsqueda, "seleccionar todo" y contador.
 * Las opciones se calculan siempre a partir de los datos, así el control
 * funciona con cualquier columna sin configuración adicional.
 */
export function MultiSelectList({
  options,
  value = [],
  onChange,
  searchPlaceholder = 'Buscar valor…',
  listClassName,
}) {
  const [query, setQuery] = useState('')

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase()
    if (!term) return options
    return options.filter((option) => String(option.label).toLowerCase().includes(term))
  }, [options, query])

  const selected = new Set(value.map(String))
  const allVisibleSelected =
    filtered.length > 0 && filtered.every((option) => selected.has(String(option.value)))

  const toggle = (optionValue, checked) => {
    const next = new Set(value.map(String))
    if (checked) next.add(String(optionValue))
    else next.delete(String(optionValue))
    onChange(Array.from(next))
  }

  const toggleAllVisible = (checked) => {
    const next = new Set(value.map(String))
    filtered.forEach((option) => {
      if (checked) next.add(String(option.value))
      else next.delete(String(option.value))
    })
    onChange(Array.from(next))
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2 rounded-lg bg-muted px-2.5">
        <SearchIcon size={14} className="shrink-0 text-muted-foreground" />
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={searchPlaceholder}
          aria-label={searchPlaceholder}
          className="h-8 w-full bg-transparent text-[13px] outline-none placeholder:text-muted-foreground"
        />
      </div>

      <div className="flex items-center justify-between px-1.5">
        <Checkbox
          checked={allVisibleSelected}
          indeterminate={!allVisibleSelected && value.length > 0}
          onChange={toggleAllVisible}
          label={query ? 'Seleccionar resultados' : 'Seleccionar todo'}
          className="px-0"
        />
        {value.length > 0 && (
          <Button variant="ghost" size="sm" onClick={() => onChange([])}>
            Limpiar
          </Button>
        )}
      </div>

      {/* `cn` no resuelve conflictos de Tailwind: la altura base solo se aplica
          si quien consume el componente no impone la suya. */}
      <ul
        className={cn(
          'scrollbar-slim overflow-y-auto border-t border-border pt-1',
          listClassName || 'max-h-56'
        )}
      >
        {filtered.length === 0 && (
          <li className="px-2 py-3 text-center text-[13px] text-muted-foreground">Sin resultados</li>
        )}
        {filtered.map((option) => (
          <li key={String(option.value)}>
            <Checkbox
              checked={selected.has(String(option.value))}
              onChange={(checked) => toggle(option.value, checked)}
              label={option.label}
              description={option.hint}
            />
          </li>
        ))}
      </ul>
    </div>
  )
}

/** Versión desplegable del selector múltiple. */
export function MultiSelect({ options, value = [], onChange, placeholder = 'Todos', className, width = 260 }) {
  const labels = options
    .filter((option) => value.map(String).includes(String(option.value)))
    .map((option) => option.label)

  const summary =
    labels.length === 0
      ? placeholder
      : labels.length === 1
        ? labels[0]
        : `${labels.length} seleccionados`

  return (
    <Popover
      width={width}
      className={cn('w-full', className)}
      renderTrigger={({ open, toggle, props }) => (
        <button
          type="button"
          onClick={toggle}
          {...props}
          className={cn(
            'flex h-[36px] w-full items-center justify-between gap-2 rounded-lg border bg-card px-3 text-left text-[14px] font-medium transition-colors',
            open ? 'border-ring' : 'border-input hover:border-ring/60'
          )}
        >
          <span className={cn('truncate', labels.length === 0 && 'font-normal text-muted-foreground')}>
            {summary}
          </span>
          <ChevronDownIcon size={15} className="shrink-0 text-muted-foreground" />
        </button>
      )}
    >
      <MultiSelectList options={options} value={value} onChange={onChange} />
    </Popover>
  )
}
